import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BookMarked, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { PromptCard } from "@/components/prompts/PromptCard";
import { PromptFormDialog } from "@/components/prompts/PromptFormDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { copyText } from "@/lib/catalogue";

export const EMPTY_TEXT = "No prompts yet for this agent. Save the first one that works.";

export function usePrompts(params) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const key = JSON.stringify(params);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/prompts", { params: JSON.parse(key) });
      setItems(data);
      setError("");
    } catch (e) {
      setError(errorMessage(e));
    }
  }, [key]);
  useEffect(() => { load(); }, [load]);
  const replace = (p) => setItems((list) => (list || []).map((x) => (x.id === p.id ? p : x)));
  return { items, error, load, replace };
}

export const AgentPrompts = ({ forId, formOpen, setFormOpen, hideList = false }) => {
  const { user } = useAuth();
  const { items, error, load, replace } = usePrompts({ for_id: forId, sort: "upvotes" });
  const [editing, setEditing] = useState(null);
  if (hideList) return <PromptFormDialog open={formOpen} onOpenChange={setFormOpen} editing={null} defaultFor={forId} onSaved={() => load()} />;
  return (
    <section data-testid="agent-prompts">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold">From the Prompt Hub</h2>
        {user.role !== "viewer" && <Button variant="secondary" size="sm" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="agent-prompts-add"><Plus className="!h-4 !w-4" /> Save a prompt</Button>}
      </div>
      {error ? <ErrorState message={error} onRetry={load} /> : !items ? <Skeleton className="h-32 rounded-xl" /> : items.length === 0 ? (
        <EmptyState icon={BookMarked} title={EMPTY_TEXT} testid="agent-prompts-empty" className="py-8" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">{items.map((p) => <PromptCard key={p.id} prompt={p} compact onChanged={replace} onEdit={(x) => { setEditing(x); setFormOpen(true); }} />)}</div>
      )}
      <PromptFormDialog open={formOpen} onOpenChange={setFormOpen} editing={editing} defaultFor={forId} onSaved={() => load()} />
    </section>
  );
};

export const SavedPromptPicker = ({ forId }) => {
  const [open, setOpen] = useState(false);
  const { items, replace } = usePrompts({ for_id: forId, sort: "upvotes" });
  const choose = async (p) => {
    if (!(await copyText(p.text))) return toast.error("Could not copy. Select the text manually.");
    try { const { data } = await api.post(`/prompts/${p.id}/copy`); replace(data); } catch { /* best effort */ }
    toast.success("Prompt copied. Paste it into the run surface.");
    setOpen(false);
  };
  return (
    <>
      <Button variant="secondary" className="w-full" onClick={() => setOpen(true)} data-testid="use-saved-prompt-button"><BookMarked className="!h-4 !w-4" /> Use a saved prompt</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto rounded-xl sm:max-w-xl" data-testid="saved-prompt-dialog">
          <DialogHeader><DialogTitle>Saved prompts for this one</DialogTitle><DialogDescription>Choose one to copy it to your clipboard.</DialogDescription></DialogHeader>
          {!items ? <Skeleton className="h-24 rounded-xl" /> : items.length === 0 ? <p className="text-sm text-muted-foreground">{EMPTY_TEXT}</p> : (
            <ul className="space-y-2">
              {items.map((p) => (
                <li key={p.id}>
                  <button type="button" onClick={() => choose(p)} className="w-full rounded-xl border p-3 text-left transition-quick hover:bg-muted" data-testid={`saved-prompt-${p.id}`}>
                    <p className="text-sm font-medium">{p.title} <span className="text-xs font-normal text-muted-foreground">{p.upvotes} upvotes · {p.copies} uses</span></p>
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{p.text}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
